import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { useAccess } from '../access.js';

const labels = { openai: 'OpenAI', gemini: 'Google Gemini', google_places: 'Google Maps' };

export default function Settings() {
  const { user, can } = useAccess();
  const client = useQueryClient();
  const [saved, setSaved] = useState('');
  const tenantQ = useQuery({
    queryKey: ['settings', user?.tenant?.id],
    queryFn: api.settings,
    enabled: Boolean(user?.tenant),
  });
  const internalQ = useQuery({
    queryKey: ['internal-settings'],
    queryFn: api.internalSettings,
    enabled: can('providers.manage'),
  });
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Configurações</h1>
        <p className="text-slate-600 mt-2">
          Provedores, credenciais e limite de consumo de {user?.tenant?.name ?? 'cada tenant'}.
        </p>
      </div>
      {saved && (
        <p role="status" className="rounded-lg bg-emerald-50 text-emerald-800 p-4">
          {saved}
        </p>
      )}
      {!user?.tenant && <p>Selecione um tenant para configurar seus provedores.</p>}
      {tenantQ.isLoading && <p>Carregando configurações…</p>}
      {tenantQ.error && (
        <p role="alert" className="text-red-600">
          {tenantQ.error.message}
        </p>
      )}
      {tenantQ.data && (
        <TenantForm
          key={`${user.tenant.id}:${tenantQ.data.revision}`}
          data={tenantQ.data}
          onSaved={(data) => {
            client.setQueryData(['settings', user.tenant.id], data);
            client.invalidateQueries({ queryKey: ['budgets'] });
            setSaved(
              'Configurações do tenant salvas. Gere novamente os planos pendentes para usar a nova configuração.',
            );
          }}
        />
      )}
      {can('providers.manage') && (
        <>
          {internalQ.error && (
            <p role="alert" className="text-red-600">
              {internalQ.error.message}
            </p>
          )}
          {internalQ.data && (
            <InternalForm
              key={internalQ.data.revision}
              data={internalQ.data}
              onSaved={(data) => {
                client.setQueryData(['internal-settings'], data);
                client.invalidateQueries({ queryKey: ['settings'] });
                setSaved(
                  'Chaves internas atualizadas. Os tenants que escolheram credenciais internas usarão essas chaves.',
                );
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function TenantForm({ data, onSaved }) {
  const [settings, setSettings] = useState(data.settings);
  const [keys, setKeys] = useState({});
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const change = (next) => {
    setSettings(next);
    setRequestId(crypto.randomUUID());
  };
  const changeKey = (provider, value) => {
    setKeys((current) => ({ ...current, [provider]: value || undefined }));
    setRequestId(crypto.randomUUID());
  };
  const save = useMutation({
    mutationFn: () => api.saveSettings({ revision: data.revision, requestId, settings, keys }),
    onSuccess: onSaved,
  });
  const llm = settings.llm;
  const models = data.catalog[llm.provider] ?? [];
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
      className="space-y-6"
    >
      <fieldset disabled={save.isPending} className="space-y-6">
        <Panel
          title="Inteligência artificial"
          description="Usada para planejar campanhas e avaliar os leads encontrados."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Provedor de IA">
              <select
                className="input"
                value={llm.provider}
                onChange={(event) => {
                  const provider = event.target.value;
                  change({
                    ...settings,
                    llm: {
                      provider,
                      model: data.catalog[provider]?.[0]?.id ?? '',
                      credentialSource: data.credentials[provider]?.internal
                        ? 'internal'
                        : 'tenant',
                    },
                  });
                }}
              >
                <option value="ollama">Ollama local</option>
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </Field>
            <Field label="Modelo">
              <select
                className="input"
                value={llm.model}
                onChange={(e) => change({ ...settings, llm: { ...llm, model: e.target.value } })}
              >
                {!models.some((model) => model.id === llm.model) && (
                  <option value={llm.model}>{llm.model} (indisponível)</option>
                )}
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {llm.provider === 'ollama' ? (
            <p className="text-sm text-emerald-800 bg-emerald-50 p-3 rounded-lg">
              Executa no servidor local da Nexify. Não precisa de chave e não cobra por chamada de
              IA.
            </p>
          ) : (
            <>
              <Source
                value={llm.credentialSource}
                available={data.credentials[llm.provider]}
                onChange={(credentialSource) =>
                  change({ ...settings, llm: { ...llm, credentialSource } })
                }
              />
              <p className="text-sm text-slate-600">
                O consumo usa a conta da chave selecionada. O limite mensal abaixo se aplica também
                às chaves próprias.
              </p>
            </>
          )}
        </Panel>
        <Panel
          title="Descoberta no Google Maps"
          description="Escolha a credencial usada para buscar empresas reais."
        >
          <Field label="Serviço de busca">
            <select
              className="input"
              value={settings.maps.provider}
              onChange={(e) =>
                change({ ...settings, maps: { ...settings.maps, provider: e.target.value } })
              }
            >
              <option value="disabled">Desativado — configurar depois</option>
              <option value="google_places">Google Maps · Places API</option>
            </select>
          </Field>
          {settings.maps.provider === 'google_places' && (
            <Source
              value={settings.maps.credentialSource}
              available={data.credentials.google_places}
              onChange={(credentialSource) =>
                change({ ...settings, maps: { ...settings.maps, credentialSource } })
              }
            />
          )}
          <p className="text-sm text-slate-600">
            A chave interna só fica disponível quando cadastrada pelo Super Admin. O Google pode
            oferecer franquias, mas a chave não garante gratuidade. As estimativas consideram a
            tarifa padrão, sem descontar franquias.
          </p>
          <p className="text-sm text-slate-500">
            Até 20 empresas por consulta. A chave precisa ter Places API habilitada e faturamento
            ativo no projeto.
          </p>
          <a
            className="text-sm text-brand-700 underline"
            target="_blank"
            rel="noreferrer"
            href="https://developers.google.com/maps/billing-and-pricing/pricing"
          >
            Consultar preços e franquias do Google
          </a>
        </Panel>
        <Panel
          title="Chaves próprias do tenant"
          description="Compartilhadas pelas campanhas deste tenant. Os valores salvos ficam protegidos no Router e não são exibidos novamente."
        >
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(labels).map(([provider, label]) => (
              <SecretField
                key={provider}
                label={label}
                configured={data.credentials[provider].tenant}
                value={keys[provider]}
                onChange={(value) => changeKey(provider, value)}
                onRemove={() => {
                  setKeys((current) => ({ ...current, [provider]: null }));
                  setRequestId(crypto.randomUUID());
                }}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Deixe em branco para manter a chave atual. Para remover uma chave em uso, selecione
            outra origem ou desative o provedor antes de salvar.
          </p>
        </Panel>
        <Panel
          title="Limite de consumo"
          description="Teto mensal em USD para chamadas de IA e Maps deste tenant."
        >
          <Field label="Limite mensal (USD)">
            <input
              type="number"
              className="input sm:max-w-xs"
              min="0"
              max="999999"
              step="0.01"
              required
              value={Number(settings.monthlyBudgetMicroUsd) / 1_000_000}
              onChange={(e) =>
                change({
                  ...settings,
                  monthlyBudgetMicroUsd: String(Math.round(Number(e.target.value) * 1_000_000)),
                })
              }
            />
          </Field>
          <p className="text-sm text-slate-600">
            Zero bloqueia chamadas pagas e permite Ollama local. Cada campanha também respeita seu
            próprio orçamento. Salvar as configurações não faz chamadas pagas nem testa o saldo das
            chaves.
          </p>
        </Panel>
        <div className="flex flex-wrap items-center gap-4">
          <button type="submit" className="btn-primary">
            {save.isPending ? 'Salvando…' : 'Salvar configurações'}
          </button>
          {save.error && (
            <p role="alert" className="text-sm text-red-600">
              {save.error.message}
            </p>
          )}
        </div>
      </fieldset>
    </form>
  );
}

function InternalForm({ data, onSaved }) {
  const [keys, setKeys] = useState({});
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const update = (provider, value) => {
    setKeys((current) => ({ ...current, [provider]: value }));
    setRequestId(crypto.randomUUID());
  };
  const save = useMutation({
    mutationFn: () => api.saveInternalSettings({ revision: data.revision, requestId, keys }),
    onSuccess: onSaved,
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <fieldset disabled={save.isPending}>
        <Panel
          title="Chaves internas · Super Admin"
          description="Disponíveis para todos os tenants que escolherem a origem interna. O consumo é cobrado na conta dessas chaves."
        >
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(labels).map(([provider, label]) => (
              <SecretField
                key={provider}
                label={label}
                configured={data.credentials[provider].internal}
                value={keys[provider]}
                onChange={(value) => update(provider, value || undefined)}
                onRemove={() => update(provider, null)}
              />
            ))}
          </div>
          <p className="text-sm text-slate-600">
            Ao remover uma chave interna, os tenants que a utilizam precisarão selecionar outra
            credencial.
          </p>
          <button
            className="btn-primary"
            type="submit"
            disabled={!Object.values(keys).some((value) => value !== undefined)}
          >
            {save.isPending ? 'Salvando…' : 'Salvar chaves internas'}
          </button>
          {save.error && (
            <p role="alert" className="text-red-600 text-sm">
              {save.error.message}
            </p>
          )}
        </Panel>
      </fieldset>
    </form>
  );
}
function Source({ value, available, onChange }) {
  return (
    <Field label="Origem da chave">
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="internal">
          Chave interna Nexify {available?.internal ? '· disponível' : '· não cadastrada'}
        </option>
        <option value="tenant">
          Chave própria do tenant {available?.tenant ? '· cadastrada' : '· cadastrar abaixo'}
        </option>
      </select>
    </Field>
  );
}
function SecretField({ label, configured, value, onChange, onRemove }) {
  return (
    <div className="space-y-2">
      <Field label={label}>
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          maxLength={512}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={configured ? 'Nova chave para substituir' : 'Cole a chave aqui'}
        />
      </Field>
      <p className="text-xs text-slate-500">
        {value === null
          ? 'Será removida ao salvar'
          : configured
            ? 'Chave cadastrada'
            : 'Nenhuma chave cadastrada'}
      </p>
      {configured && (
        <button type="button" className="text-xs text-red-700 underline" onClick={onRemove}>
          Remover chave de {label}
        </button>
      )}
    </div>
  );
}
function Panel({ title, description, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
      <div>
        <h2 className="font-semibold">{title}</h2>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block mb-1 font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
